// annotation.component.ts
import { Component, ElementRef, HostListener, OnInit, Output, EventEmitter, QueryList, ViewChild, ViewChildren, Input, Optional } from '@angular/core';
import { timeStamp } from 'console';
import { Annotations, AnnotationSpeaker } from 'src/app/models/StreamingModels';
import { BackendService } from 'src/app/services/backend.service';
import { AudiostreamingListComponent } from '../audiostreaming-list/audiostreaming-list.component';
import { Number } from 'aws-sdk/clients/iot';


@Component({
  selector: 'app-annotation',
  templateUrl: './annotation.component.html',
  styleUrls: ['./annotation.component.css']
})
export class AnnotationComponent implements OnInit {
  @ViewChildren('commentInput') commentInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('timeStampInput') timeStampInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('annotationTableContainer') annotationTableContainer?: ElementRef<HTMLDivElement>;

  @Output() toggleMaximize = new EventEmitter<boolean>();
  @Output() updateAudioPosition = new EventEmitter<Number>();

  annotationsList: Annotations[] = [];
  private allAnnotations: Annotations[] = [];
  nextRowId = 0;
  showActionMenu = false;
  @Input() recordingDuration: string = '00:00:00';
  @Input() pausedAt: number | null = null;
  @Input() recordingId: number = 0;
  @Input() filterByCaseDetailsId: boolean = true;
  private selectedCaseDetailsId: number | null = null;
  @Input()
  set caseDetailsId(value: number | null) {
    this.selectedCaseDetailsId = value == null ? null : Number(value);
    this.applyCaseDetailsFilter();
  }
  get caseDetailsId(): number | null {
    return this.selectedCaseDetailsId;
  }
  @Input() autoStartTranscription:boolean = false;
  @Input() postRecordingAnnotationUpdate:boolean = false;
  @Input() startDate: string = '';

  // Speakers from the selected case's reference-linked form fields. Judge, Defendant and Clerk get a
  // fixed Ctrl + Shift + J / D / C; every other speaker gets Ctrl + Shift + 1-9 in list order.
  // Speakers past the ninth numbered one are only reachable from the Speakers menu.
  speakerShortcuts: { speaker: AnnotationSpeaker; digit: string | null; letter: string | null }[] = [];
  showSpeakerMenu = false;
  private static readonly ROLE_SHORTCUT_LETTERS: Record<string, string> = {
    'judge': 'J',
    'defendant': 'D',
    'clerk': 'C'
  };
  @Input()
  set speakers(value: AnnotationSpeaker[] | null) {
    const usedLetters = new Set<string>();
    let nextDigit = 1;
    this.speakerShortcuts = (value || []).map(speaker => {
      const role = (speaker.role || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const roleLetter = AnnotationComponent.ROLE_SHORTCUT_LETTERS[role];
      // A second field for the same role can't share the letter, so it falls back to a number.
      if (roleLetter && !usedLetters.has(roleLetter)) {
        usedLetters.add(roleLetter);
        return { speaker, digit: null, letter: roleLetter };
      }
      const digit = nextDigit <= 9 ? String(nextDigit++) : null;
      return { speaker, digit, letter: null };
    });
    if (!this.speakerShortcuts.length) {
      this.showSpeakerMenu = false;
    }
  }

  isMaximized = false;
  isOpenedByAudioStreamingList = false;
  
  sidebarActive = false;
  decisionActive = false;
  dispositionActive = false;
  deleteConfirmationId: number | null = null;
  selectedTimeSource: string = 'audio'; // Default to 'audio'
  selectedTimeStamp: string = ''; // Default to empty string
  isDisabled = false;
  showMaximizeButton = false;

  constructor(private backendService: BackendService,
    @Optional() private audiostreamingListComponent: AudiostreamingListComponent
  ) {
    if (this.audiostreamingListComponent) {
      this.isOpenedByAudioStreamingList = true;
    }
  }

  ngOnInit(): void {
    
    if (this.isOpenedByAudioStreamingList) {
      this.selectedTimeStamp = 'Player'; // Default to 'Player' if opened by AudioStreamingList
    }
    this.annotationsList = [];
    this.loadAnnotationData(this.recordingId);
    this.isDisabled = this.isOpenedByAudioStreamingList && !this.postRecordingAnnotationUpdate ? true : false;  
    if(this.isOpenedByAudioStreamingList){
      this.showMaximizeButton = true;
    }
    else
    {
      this.showMaximizeButton = this.autoStartTranscription ? true : false;
    }
  }
 ngOnDestroy(): void {
    this.annotationsList = [];
    this.postRecordingAnnotationUpdate = false;
    this.isOpenedByAudioStreamingList = false;
    this.autoStartTranscription= false;
    this.isDisabled = false;
    this.isMaximized = false;
  };
  refreshAnnotations(): void {
    // console.log('Refresh annotations clicked.');
    this.loadAnnotationData(this.recordingId);
    // TODO: wire to current recording id or reload logic
  }

  toggleMaximizeView(): void {
    this.isMaximized = !this.isMaximized;
    console.log(this.isMaximized ? 'Maximize view activated.' : 'Minimize view activated.');
    this.toggleMaximize.emit(this.isMaximized);
  }

  async loadAnnotationData(recordingId: number): Promise<void> {
    const AnnotationsList = this.backendService.getJSONResponse(await this.backendService.getAnnotationsbyRecordingId(recordingId).toPromise());
    // console.log('Fetched annotations:', AnnotationsList);
    this.allAnnotations = AnnotationsList.data.map((annotation: any) => ({
      id: annotation.id,
      timeStamp: this.formatTimeToLocal(annotation.timeStamp),
      speaker: annotation.speaker,
      comments: annotation.comments,
      recordingSessionId: annotation.recordingSessionId,
      sourceType: annotation.sourceType,
      audioTime: annotation.audioTime,
      caseDetailsId: annotation.caseDetailsId ?? annotation.CaseDetailsId ?? null
    }));
    this.applyCaseDetailsFilter();
  };

  private applyCaseDetailsFilter(): void {
    this.annotationsList = !this.filterByCaseDetailsId || this.selectedCaseDetailsId === null
      ? [...this.allAnnotations]
      : this.allAnnotations.filter(annotation => annotation.caseDetailsId === this.selectedCaseDetailsId);
  }

  private formatTimeToLocal(timeString: string): string {
    try {
      if (!timeString) {
        return '';
      }

      const utcIso = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}:\d{2})?$/;
      const timeOnly = /^\d{2}:\d{2}:\d{2}$/;
      let date: Date;

      if (utcIso.test(timeString)) {
        const match = timeString.match(utcIso);
        if (match) {
          const hasOffset = !!match[2];
          date = new Date(hasOffset ? timeString : `${match[1]}Z`);
        } else {
          date = new Date(timeString);
        }
      } else if (timeOnly.test(timeString)) {
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        date = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), hours, minutes, seconds));
      } else {
        date = new Date(timeString);
      }

      if (isNaN(date.getTime())) {
        return timeString; // Return as-is if invalid
      }

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString; // Fallback to original string
    }
  }

  // editRow(row: Annotation): void {
  //   row.isEditing = true;
  // }

  async deleteRow(id: number) {
    console.log('Deleting row with id:', id);
    this.annotationsList = this.annotationsList.filter(item => item.id !== id);
    this.deleteConfirmationId = null;
    if (id > 0) {
      const row: Annotations = {
        id: id,
        timeStamp: this.formatTime(),
        speaker: '',
        comments: '',
        recordingSessionId: this.recordingId,
        sourceType: 'Web',
        audioTime: 0,
        isEditing: true
      };
      var response = await this.backendService.deleteAnnotation(row).toPromise();
      this.refreshAnnotations();
    }
  }

  confirmDelete(id: number): void {
    this.deleteConfirmationId = id;
  }

  cancelDelete(): void {
    this.deleteConfirmationId = null;
  }

  onActionSelected(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const action = selectElement.value;

    switch (action) {
      case 'sidebar':
        this.addRow('Sidebar');
        break;
      case 'annotation':
        this.addRow('Annotation');
        break;
      case 'decision':
        this.addRow('Decision');
        break;
      case 'disposition':
        this.addRow('Disposition');
        break;
      case 'timestamp':
        this.addRow('Time Stamp');
        break;
    }

    if (selectElement.value) {
      selectElement.value = '';
    }
  }

  toggleActionMenu(): void {
    this.showActionMenu = !this.showActionMenu;
    this.showSpeakerMenu = false;
  }

  toggleSpeakerMenu(): void {
    this.showSpeakerMenu = !this.showSpeakerMenu;
    this.showActionMenu = false;
  }

  selectSpeaker(speaker: AnnotationSpeaker): void {
    this.showSpeakerMenu = false;
    // Shown in the annotation row as "Judge: Ajay" so the speaker's role stays visible.
    const role = (speaker.role || speaker.label || '').trim();
    this.addRow(role ? `${role}: ${speaker.name}` : speaker.name);
  }

  // event.code, not event.key: with Shift held, key reports '!' for 1 and so on.
  private handleSpeakerShortcut(event: KeyboardEvent): boolean {
    if (!this.speakerShortcuts.length) {
      return false;
    }
    const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code)?.[1];
    const letter = /^Key([A-Z])$/.exec(event.code)?.[1];
    const match = this.speakerShortcuts.find(s =>
      (digit && s.digit === digit) || (letter && s.letter === letter));
    if (!match) {
      return false;
    }
    event.preventDefault();
    this.selectSpeaker(match.speaker);
    return true;
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && !event.altKey && !this.isTypingInEditableField(event)) {
      this.handleSpeakerShortcut(event);
      return;
    }
    if (event.ctrlKey && !this.isTypingInEditableField(event)) {
      switch (event.key.toLowerCase()) {
        case 'u':
          event.preventDefault();
          this.selectAction(this.sidebarActive ? 'Stop Sidebar' : 'Start Sidebar');
          break;
        case 'e':
          event.preventDefault();
          this.selectAction(this.decisionActive ? 'Stop Decision' : 'Start Decision');
          break;
        case 'y':
          event.preventDefault();
          this.selectAction('Disposition');
          break;
        case 'q':
          event.preventDefault();
          this.selectAction('Annotation');
          break;
        case 'm':
          event.preventDefault();
          this.selectAction('Time Stamp');
          break;
      }
    }
  }

  private isTypingInEditableField(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return false;
    }

    const tagName = target.tagName.toLowerCase();
    const editable = target.getAttribute('contenteditable');

    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      editable === 'true'
    );
  }

  selectAction(action: string): void {
    this.showActionMenu = false;

    switch (action) {
      case 'Start Sidebar':
        if (!this.sidebarActive) {
          this.sidebarActive = true;
          this.addRow(action);
        }
        break;
      case 'Stop Sidebar':
        if (this.sidebarActive) {
          this.sidebarActive = false;
          this.addRow(action);
        }
        break;
      case 'Start Decision':
        if (!this.decisionActive) {
          this.decisionActive = true;
          this.addRow(action);
        }
        break;
      case 'Stop Decision':
        if (this.decisionActive) {
          this.decisionActive = false;
          this.addRow(action);
        }
        break;
      // case 'Start Disposition':
      //   if (!this.dispositionActive) {
      //     this.dispositionActive = true;
      //     this.addRow(action);
      //   }
      //   break;
      // case 'Stop Disposition':
      //   if (this.dispositionActive) {
      //     this.dispositionActive = false;
      //     this.addRow(action);
      //   }
      //   break;
      case 'Disposition':
      case 'Annotation':
      case 'Time Stamp':
        this.addRow(action);
        break;
      default:
        console.warn('Unknown annotation action:', action);
    }
  }

  private parseDurationToSeconds(duration: string): number {
    if (!duration || typeof duration !== 'string') {
      return 0;
    }

    return duration
      .split(':')
      .map(part => parseInt(part, 10) || 0)
      .reduce((acc, time) => (acc * 60) + time, 0);
  }

  private resolveCaseDetailsId(timeStamp: string): number | null {
    if (this.selectedCaseDetailsId !== null) {
      return this.selectedCaseDetailsId;
    }

    const annotationTime = this.parseDurationToSeconds(timeStamp);
    return this.allAnnotations
      .filter(annotation => annotation.caseDetailsId !== null && annotation.audioTime <= annotationTime)
      .sort((first, second) => second.audioTime - first.audioTime)[0]?.caseDetailsId ?? null;
  }

  addRow(type: string): void {
    console.log("recordingDuration", this.recordingDuration);
    const totalSeconds: number = this.parseDurationToSeconds(this.recordingDuration);
    let timeStamp = this.formatTime();
    const defaultComment = type === 'Time Stamp' ? `${type} ${timeStamp}` : '';
    const newRow: Annotations = {
      id: 0,
      timeStamp: timeStamp,
      speaker: type,
      comments: defaultComment,
      recordingSessionId: this.recordingId,
      sourceType: 'Web',
      audioTime: totalSeconds,
      caseDetailsId: this.resolveCaseDetailsId(timeStamp),
      isEditing: true,
      // isNewManualRow: true
    };

    this.annotationsList = [...this.annotationsList, newRow];
    setTimeout(() => {
      if (this.selectedTimeStamp === 'Manual') {
        this.focusLastTimeStampInput()
      }
      else {
        this.focusLastCommentInput();
      }
      this.scrollToLatestAnnotation();
    }, 0);
  }

  private scrollToLatestAnnotation(annotationId?: number): void {
    const container = this.annotationTableContainer?.nativeElement;
    if (!container) {
      return;
    }
    const row = annotationId
      ? container.querySelector<HTMLElement>(`[data-annotation-id="${annotationId}"]`)
      : null;
    if (row) {
      const containerBounds = container.getBoundingClientRect();
      const rowBounds = row.getBoundingClientRect();
      const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height || 0;
      if (rowBounds.bottom > containerBounds.bottom) {
        container.scrollTop += rowBounds.bottom - containerBounds.bottom;
      } else if (rowBounds.top < containerBounds.top + headerHeight) {
        container.scrollTop += rowBounds.top - containerBounds.top - headerHeight;
      }
    } else if (!annotationId) {
      container.scrollTop = container.scrollHeight;
    }
  }

  // private focusLastCommentInput(): void {
  //   const inputs = this.commentInputs?.toArray();
  //   // console.log('TimeStamp Inputs:', inputs);
  //   if (!inputs || inputs.length === 0) {
  //     return;
  //   }

  //   const lastInput = inputs[inputs.length - 1]?.nativeElement;
  //   // console.log('Last TimeStamp Input:', lastInput);
  //   if (lastInput && typeof lastInput.focus === 'function') {
  //     lastInput.focus();
  //     lastInput.select();
  //   }
  // }
  private focusLastCommentInput(): void {
  const inputs = this.commentInputs?.toArray();
  // console.log('TimeStamp Inputs:', inputs);
  if (!inputs || inputs.length === 0) {
    return;
  }

  const lastInput = inputs[inputs.length - 1]?.nativeElement as HTMLInputElement;
  // console.log('Last TimeStamp Input:', lastInput);
  if (lastInput && typeof lastInput.focus === 'function') {
    // 1. Focus the input field
    lastInput.focus({ preventScroll: true });
    
    // 2. Safely cast and move the cursor to the end of the existing text
    const valLength = lastInput.value ? lastInput.value.length : 0;
    if (typeof lastInput.setSelectionRange === 'function') {
      lastInput.setSelectionRange(valLength, valLength);
    }
  }
}
  private focusLastTimeStampInput(): void {
    setTimeout(() => {
      const inputs = this.timeStampInputs?.toArray();
      if (!inputs || inputs.length === 0) {
        return;
      }

      const lastInput = inputs[inputs.length - 1]?.nativeElement;
      if (lastInput && typeof lastInput.focus === 'function') {
        lastInput.focus({ preventScroll: true });
        lastInput.select();
      }
    }, 0);
  }
  private focusCommentInputOfRow(currentRow: any): void {
    // Find the exact row index in your active list
    const index = this.annotationsList.indexOf(currentRow);
    console.log('Current Row Index:', index, 'Current Row:', currentRow);

    if (index !== -1) {
      // Use setTimeout to ensure the DOM state stabilizes before assigning focus
      setTimeout(() => {
        const commentElements = this.commentInputs?.toArray();
        const lastInput = commentElements[commentElements.length - 1]?.nativeElement;
        // console.log('Last TimeStamp Input:', lastInput);
        if (lastInput && typeof lastInput.focus === 'function') {
          lastInput.focus();
          lastInput.select();
        }
      }, 50);
    }
  }
  private isSystemInitRow(row: Annotations): boolean {
    return !!row.speaker && row.speaker.toLowerCase().includes('system init');
  }

  onRowClick(row: Annotations): void {
    this.changeAudioPositionInPlayer(row);
  }

  // Emitted to whichever host owns the player (audiostreaming-list or audiostreaming).
  changeAudioPositionInPlayer(row: Annotations): void {
    if (!row || row.audioTime === null || row.audioTime === undefined || String(row.audioTime).trim() === '') {
      return;
    }
    const audioTime = Number(row.audioTime);
    if (!Number.isFinite(audioTime) || audioTime < 0) {
      return;
    }

    this.updateAudioPosition.emit(audioTime);
  }

  startRowEdit(row: Annotations): void {
    if (this.isSystemInitRow(row)) {
      return;
    }
    if (this.isOpenedByAudioStreamingList && !this.postRecordingAnnotationUpdate)
    {
      return;
    }
    if(row.speaker.includes("System"))
    {
      return;
    }
    this.isOpenedByAudioStreamingList ? this.updateAudioPosition.emit(row.audioTime) : null;
    this.annotationsList.forEach(item => {
      if (item.id !== row.id) {
        item.isEditing = false;
      }
    });

    row.isEditing = true;
    setTimeout(() => this.focusLastCommentInput(), 0);
  }

  saveRow(row: Annotations): void {
    console.log("saveRow ",row);
    if (row.isEditing) {
      row.isEditing = false;
      row.caseDetailsId = this.resolveCaseDetailsId(row.timeStamp);
      //if (row.comments.trim()) {
        console.log('Saving row:', row);
        this.addAnnotation(row);
      //}
    }
  }
  

  onCommentInputKeyDown(event: KeyboardEvent, row: Annotations): void {
    if (event.key === 'Enter' || event.key === 'Tab') {
      if (event.key === 'Enter' || event.key === 'Tab' ) {
        event.preventDefault();
      }
      this.saveRow(row);
    }
  }
  onTimeStampInputKeyDown(event: KeyboardEvent, row: any): void {
    if (event.key === 'Enter' || event.key === 'Tab') {
      // 1. Validate the time format first
      if (!this.validateTimeFormat(row.timeStamp)) {
        row.isTimeInvalid = true;
        event.preventDefault(); // Lock them in this cell until it's fixed
        return;
      }

      // 2. Format is valid, clear any errors
      row.isTimeInvalid = false;
      row.isNewManualRow = false; // Mark this row as no longer new
      event.preventDefault(); // Stop default browser tab/newline jumps

      // 3. Move focus to the comment input in the same row
      //this.focusCommentInputOfRow(row);
      this.focusLastCommentInput(); // Focus the last comment input for simplicity
      // 4. Transform timestamp cell back into a label
      // row.isEditing = false;
    }
  }

  onTimeStampInputBlur(row: any): void {
    // Handles mouse clicks outside the input cell
    if (this.validateTimeFormat(row.timeStamp)) {
      row.isTimeInvalid = false;
      row.isEditing = false;
    } else {
      row.isTimeInvalid = true;
    }
  }
  onTimeInput(event: Event, row: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // 1. Remove all non-numeric characters
    let formatted = '';

    // 2. Build the HH:mm:ss mask dynamically
    if (value.length > 0) {
      // Hour validation: Max 23
      let hours = value.substring(0, 2);
      if (hours.length === 2 && parseInt(hours, 10) > 23) {
        hours = '23';
      }
      formatted += hours;
    }

    if (value.length > 2) {
      // Minute validation: Max 59
      let minutes = value.substring(2, 4);
      if (minutes.length === 2 && parseInt(minutes, 10) > 59) {
        minutes = '59';
      }
      formatted += ':' + minutes;
    }

    if (value.length > 4) {
      // Second validation: Max 59
      let seconds = value.substring(4, 6);
      if (seconds.length === 2 && parseInt(seconds, 10) > 59) {
        seconds = '59';
      }
      formatted += ':' + seconds;
    }

    // 3. Update both the DOM input value and the Angular model
    input.value = formatted;
    row.timeStamp = formatted;
  }
  private validateTimeFormat(time: string): boolean {
    if (!time) return false;
    // Strict 24-hour format validation (00:00:00 to 23:59:59)
    const regex24h = /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    return regex24h.test(time.trim());
  }
  formatTime(): string {
    console.log("Selected Timestamp:", this.selectedTimeStamp);
    if (this.selectedTimeStamp === 'Player') {
      // this.getCurrentPlayerTime();
      return this.getCurrentPlayerTime();
    } else if (this.selectedTimeStamp === 'Manual') {
      return "00:00:00";
    } else {
      const now = new Date(this.pausedAt ?? Date.now());
      const pad = (value: number) => value.toString().padStart(2, '0');
      return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    }
  }
  getCurrentPlayerTime() {
    console.log("getCurrentPlayerTime called");
    if (this.audiostreamingListComponent) {
      const currentTime = this.audiostreamingListComponent.getCurrentPlayerTime();
      console.log("Current player time from AudioStreamingListComponent:", currentTime);
      return currentTime;
    } else {
      console.warn("AudioStreamingListComponent is not available.");
      return "00:00:00";
    }
  }
  //Add or Edit Annotation
  async addAnnotation(annotationdetails: Annotations) {
    try {
      // console.log("Add/edit annotation in ts:", annotationdetails);
      const annotationrecord = { ...annotationdetails };
      // var annotationrecord = annotationdetails;
      // Convert timeStamp to UTC HH:mm:ss before sending
      try {
        const timePart = (annotationrecord.timeStamp || '').trim();
        const hhmmssRegex = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

        // Determine base date from startDate; fall back to current date if missing/invalid
        let baseDate = new Date(this.startDate || '');
        if (isNaN(baseDate.getTime())) {
          baseDate = new Date();
        }

        // Use the UTC *date* components from startDate (so date portion comes from that value)
        const utcYear = baseDate.getUTCFullYear();
        const utcMonth = baseDate.getUTCMonth(); // zero-based
        const utcDay = baseDate.getUTCDate();

        if (hhmmssRegex.test(timePart)) {
          const [hh, mm, ss] = timePart.split(':').map(p => parseInt(p, 10));

          // Construct a Date object treating the provided time as local time on the
          // wall-clock date extracted from `startDate`'s UTC date. We build a local
          // Date using the same Y/M/D but in local timezone, then convert to UTC.
          const localDate = new Date(utcYear, utcMonth, utcDay, hh, mm, ss);

          const pad = (n: number) => n.toString().padStart(2, '0');
          annotationrecord.timeStamp = `${localDate.getUTCFullYear()}-${pad(localDate.getUTCMonth() + 1)}-${pad(localDate.getUTCDate())}T${pad(localDate.getUTCHours())}:${pad(localDate.getUTCMinutes())}:${pad(localDate.getUTCSeconds())}`;
        } else {
          // Fallback: use current UTC time on the base date
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          const localDate = new Date(utcYear, utcMonth, utcDay, now.getHours(), now.getMinutes(), now.getSeconds());
          annotationrecord.timeStamp = `${localDate.getUTCFullYear()}-${pad(localDate.getUTCMonth() + 1)}-${pad(localDate.getUTCDate())}T${pad(localDate.getUTCHours())}:${pad(localDate.getUTCMinutes())}:${pad(localDate.getUTCSeconds())}`;
        }
      } catch (err) {
        // If conversion fails, fallback to current UTC time
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        annotationrecord.timeStamp = `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
      }

      // 1. Wait for the server to finish saving/updating
      //console.log("Saving annotation:", annotationrecord.timeStamp);
      var response = await this.backendService.saveAnnotation(annotationrecord).toPromise();
        var result = this.backendService.getJSONResponse(response);
      // console.log('💾 New Add/edit Annotation result', result);

      // 2. Trigger the list reload immediately
      //await this.reloadAndSyncPopupTable(annotationdetails.recordingSessionId);
      this.recordingId = annotationrecord.recordingSessionId; // Update the recordingId
      await this.loadAnnotationData(result.data.recordingSessionId);
      if (!annotationdetails.id) {
        setTimeout(() => this.scrollToLatestAnnotation(result.data.id), 0);
      }
      return result?.data as Annotations;
    } catch (error) {
      //console.error("Error saving annotation:", error);
    }
    return null;
  }
  /** Latest annotation matching a speaker/comment pair, used to recover an id the save response omitted. */
  findAnnotationId(speaker: string, comments: string): number | null {
    for (let i = this.allAnnotations.length - 1; i >= 0; i--) {
      const annotation = this.allAnnotations[i];
      if (annotation.speaker === speaker && annotation.comments === comments) {
        return annotation.id ?? null;
      }
    }
    return null;
  }

  async deleteAnnotation(annotationdetails: Annotations) {
    try {
      console.log("delete annotation in ts:", annotationdetails);
      if(this.isOpenedByAudioStreamingList && !this.postRecordingAnnotationUpdate)
      {
        return;
      }
      // 1. Wait for the server to delete the record
      var response = await this.backendService.deleteAnnotation(annotationdetails).toPromise();
      var result = this.backendService.getJSONResponse(response);
      console.log('💾 delete Annotation result', response);

      // 2. Trigger the list reload immediately
      await this.loadAnnotationData(result.data.recordingSessionId);
    } catch (error) {
      console.error("Error deleting annotation:", error);
    }
  }
  //  selectTimeSource(element:any): void {
  //         if (element.dataset.disabled === 'true') return;
  //         document.querySelectorAll('.radio-btn').forEach(btn => btn.classList.remove('selected'));
  //         element.classList.add('selected');
  //         this.selectedTimeSource = element.dataset.value;
  //     }
  selectTimeSource(selectedTimeStamp: any) {
    console.log("Clicked value:", selectedTimeStamp);
    this.selectedTimeStamp = selectedTimeStamp;
    // 1. Find any button that currently has the 'selected' class and remove it
    const currentlySelected = document.querySelector('.timestamp-btn.selected');
    if (currentlySelected) {
      currentlySelected.classList.remove('selected');
    }
    // 2. Safely convert string to lowercase to ensure it matches the data-value attribute
    const targetValue = String(selectedTimeStamp).toLowerCase();

    // 3. Find the specific button element that has data-value="player" or "manual"
    const targetButton = document.querySelector(`.timestamp-btn[data-value="${targetValue}"]`);

    // 4. Append the 'selected' class to that specific button
    if (targetButton) {
      targetButton.classList.add('selected');
    } else {
      console.warn(`No button found with data-value="${targetValue}"`);
    }
  }
}